import React, { useState, useCallback, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL || '';
const DEFAULT_VERSION = 'NCC2111';

function getToken() {
  return localStorage.getItem('notes_token');
}
async function apiFetch(url, opts = {}) {
  const token = getToken();
  const res = await fetch(`${API_URL}${url}`, { ...opts, headers: { ...(opts.headers || {}), 'x-token': token } });
  if (res.status === 401) { localStorage.removeItem('notes_token'); localStorage.removeItem('notes_user'); alert('登录超时，请重新登录'); window.location.href = '/'; return null; }
  return res;
}

const TABS = [
  { key: 'table', label: '表结构查询', icon: '🔍' },
  { key: 'column', label: '字段反查', icon: '🔎' },
  { key: 'sql', label: 'SQL生成', icon: '⚡' }
];

export default function DataDictionary() {
  const [versions, setVersions] = useState([]);
  const [version, setVersion] = useState(DEFAULT_VERSION);
  const [activeTab, setActiveTab] = useState('table');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [expandedTable, setExpandedTable] = useState(null);
  const [sqlResult, setSqlResult] = useState(null);
  const [logResult, setLogResult] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  // 加载版本列表
  useEffect(() => {
    apiFetch('/api/dictionary/versions').then(res => res && res.json()).then(data => {
      if (data && data.success) setVersions(data.versions);
    });
  }, []);

  // 表结构查询
  const searchTables = useCallback(async () => {
    if (!query.trim()) return;
    setLoading(true);
    try {
      const res = await apiFetch(`/api/dictionary/search?q=${encodeURIComponent(query)}&version=${version}`);
      const data = await res.json();
      setResults(data);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }, [query, version]);

  // 字段反查
  const searchColumns = useCallback(async () => {
    if (!query.trim()) return;
    setLoading(true);
    try {
      const res = await apiFetch(`/api/dictionary/column?q=${encodeURIComponent(query)}&version=${version}`);
      const data = await res.json();
      setResults(data);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }, [query, version]);

  // SQL生成
  const generateSQL = useCallback(async () => {
    if (!query.trim()) return;
    setLoading(true);
    try {
      const res = await apiFetch('/api/dictionary/sql', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query, version })
      });
      const data = await res.json();
      setSqlResult(data);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }, [query, version]);

  const handleSearch = () => {
    setExpandedTable(null);
    setSqlResult(null);
    if (activeTab === 'log') return;
    if (activeTab === 'table') searchTables();
    else if (activeTab === 'column') searchColumns();
    else if (activeTab === 'sql') generateSQL();
  };

  // 日志分析
  const analyzeLog = async () => {
    if (!selectedFile) return;
    setLoading(true);
    setLogResult(null);
    const formData = new FormData();
    formData.append('log', selectedFile);
    formData.append('version', version);
    try {
      const res = await apiFetch('/api/dictionary/analyze-log', {
        method: 'POST',
        body: formData
      });
      const data = await res.json();
      setLogResult(data);
    } catch (e) {
      setLogResult({ success: false, error: e.message });
    }
    setLoading(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  // 展开表的完整结构
  const loadFullTable = async (tableName) => {
    if (expandedTable === tableName) {
      setExpandedTable(null);
      return;
    }
    try {
      const res = await apiFetch(`/api/dictionary/table/${encodeURIComponent(tableName)}?version=${version}`);
      const data = await res.json();
      if (data.success) {
        setExpandedTable(data.table.name);
        // 更新results中的该表
        setResults(prev => ({
          ...prev,
          tables: prev.tables.map(t =>
            t.name === tableName ? { ...t, columns: data.table.columns } : t
          )
        }));
      }
    } catch (e) {
      console.error(e);
    }
  };

  const placeholderMap = {
    table: '输入表名或中文关键词，如"用户"、"凭证"',
    column: '输入字段名或字段说明，如"PK_ORG"、"金额"',
    sql: '输入中文需求，如"查询员工的部门和薪资"',
    log: '上传HTML格式的SPR日志文件'
  };

  return (
    <div className="dictionary-page">
      {/* 版本选择 */}
      <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
        <label style={{ fontWeight: '500', color: '#374151' }}>数据字典版本：</label>
        <select
          value={version}
          onChange={e => { setVersion(e.target.value); setResults(null); setSqlResult(null); }}
          style={{
            padding: '8px 16px',
            borderRadius: '8px',
            border: '2px solid #e5e7eb',
            fontSize: '14px',
            background: '#fff',
            cursor: 'pointer',
            outline: 'none'
          }}
        >
          {versions.map(v => (
            <option key={v.id} value={v.id}>{v.label}</option>
          ))}
        </select>
        <span style={{ fontSize: '13px', color: '#6b7280' }}>
          {version === 'NCC2111' ? '5622 个表' : '5247 个表'}
        </span>
      </div>

      {/* Tab切换 */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', borderBottom: '2px solid #e8e8e8', paddingBottom: '12px' }}>
        {TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => { setActiveTab(tab.key); setResults(null); setSqlResult(null); setQuery(''); }}
            style={{
              padding: '8px 20px',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              background: activeTab === tab.key ? '#4f46e5' : '#f3f4f6',
              color: activeTab === tab.key ? '#fff' : '#374151',
              fontSize: '14px',
              fontWeight: activeTab === tab.key ? '600' : '400'
            }}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* 搜索栏 / 上传区 */}
      {activeTab === 'log' ? (
        <div style={{ marginBottom: '20px' }}>
          <div style={{
            border: '2px dashed #d1d5db', borderRadius: '12px', padding: '30px', textAlign: 'center',
            background: '#f9fafb', cursor: 'pointer', transition: 'border-color 0.2s'
          }} onClick={() => document.getElementById('log-file-input').click()}
            onMouseEnter={e => e.currentTarget.style.borderColor = '#4f46e5'}
            onMouseLeave={e => e.currentTarget.style.borderColor = '#d1d5db'}>
            <input id="log-file-input" type="file" accept=".html,.htm" style={{ display: 'none' }}
              onChange={e => setSelectedFile(e.target.files[0])} />
            {selectedFile ? (
              <div style={{ color: '#4f46e5' }}>
                <div style={{ fontSize: '24px', marginBottom: '8px' }}>📄</div>
                <div>{selectedFile.name}</div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>{(selectedFile.size / 1024).toFixed(1)} KB · 点击更换</div>
              </div>
            ) : (
              <div style={{ color: '#6b7280' }}>
                <div style={{ fontSize: '24px', marginBottom: '8px' }}>📁</div>
                <div>点击上传SPR日志文件（HTML格式）</div>
                <div style={{ fontSize: '12px', marginTop: '4px' }}>支持 .html / .htm 格式</div>
              </div>
            )}
          </div>
          <button
            onClick={analyzeLog}
            disabled={loading || !selectedFile}
            style={{
              marginTop: '14px', padding: '10px 28px', border: 'none', borderRadius: '10px',
              background: loading || !selectedFile ? '#9ca3af' : '#4f46e5',
              color: '#fff', fontSize: '14px', cursor: loading ? 'wait' : 'pointer', fontWeight: '500', width: '100%'
            }}>
            {loading ? '分析中...' : '📋 分析日志'}
          </button>
        </div>
      ) : (
        <React.Fragment>
        <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
          <input
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholderMap[activeTab]}
          style={{
            flex: 1, padding: '10px 16px', border: '2px solid #e5e7eb',
            borderRadius: '10px', fontSize: '14px', outline: 'none'
          }}
          onFocus={e => e.target.style.borderColor = '#4f46e5'}
          onBlur={e => e.target.style.borderColor = '#e5e7eb'}
        />
        <button
          onClick={handleSearch}
          disabled={loading || !query.trim()}
          style={{
            padding: '10px 28px', border: 'none', borderRadius: '10px',
            background: loading || !query.trim() ? '#9ca3af' : '#4f46e5',
            color: '#fff', fontSize: '14px', cursor: loading ? 'wait' : 'pointer',
            fontWeight: '500'
          }}
        >
          {loading ? '搜索中...' : '搜索'}
        </button>
        </div>
        </React.Fragment>
      )}

      {/* SQL生成结果 */}
      {activeTab === 'sql' && sqlResult && (
        <div style={{ marginBottom: '20px' }}>
          {sqlResult.sql && (
            <div style={{ background: '#1e1e2e', borderRadius: '12px', padding: '20px', marginBottom: '16px', overflow: 'auto' }}>
              <div style={{ color: '#cdd6f4', fontSize: '12px', marginBottom: '8px' }}>SQL</div>
              <pre style={{ color: '#a6e3a1', margin: 0, fontSize: '13px', fontFamily: 'JetBrains Mono, Consolas, monospace', whiteSpace: 'pre-wrap' }}>
                {sqlResult.sql}
              </pre>
            </div>
          )}
          {sqlResult.explanation && (
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px', padding: '14px', fontSize: '13px', color: '#166534' }}>
              <strong>说明：</strong>
              <pre style={{ margin: '4px 0 0', whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>{sqlResult.explanation}</pre>
            </div>
          )}
          {sqlResult.matchedTables && sqlResult.matchedTables.length > 0 && (
            <div style={{ marginTop: '12px', fontSize: '13px', color: '#6b7280' }}>
              匹配到 {sqlResult.matchedTables.length} 个相关表：
              {sqlResult.matchedTables.map((t, i) => (
                <span key={i} style={{ display: 'inline-block', background: '#f3f4f6', borderRadius: '6px', padding: '3px 10px', margin: '3px 4px' }}>
                  {t.name} <span style={{ color: '#9ca3af' }}>({t.desc})</span>
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* 日志分析结果 */}
      {activeTab === 'log' && logResult && (
        <div style={{ marginTop: '20px' }}>
          {logResult.analysis && (
            <div style={{ background: '#fff', border: '2px solid #e5e7eb', borderRadius: '12px', padding: '20px', marginBottom: '16px' }}>
              <div style={{ fontSize: '15px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>🤖 AI 分析结果</div>
              <div style={{ fontSize: '14px', color: '#374151', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>{logResult.analysis}</div>
            </div>
          )}
          {logResult.errors && logResult.errors.length > 0 && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', padding: '14px', marginBottom: '12px' }}>
              <div style={{ fontSize: '13px', fontWeight: '500', color: '#991b1b', marginBottom: '8px' }}>⚠️ 提取的错误信息</div>
              {logResult.errors.map((e, i) => (
                <div key={i} style={{ fontSize: '12px', color: '#7f1d1d', marginTop: '4px', whiteSpace: 'pre-wrap' }}>{e}</div>
              ))}
            </div>
          )}
          {logResult.sqls && logResult.sqls.length > 0 && (
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px', padding: '14px', marginBottom: '12px' }}>
              <div style={{ fontSize: '13px', fontWeight: '500', color: '#166534', marginBottom: '8px' }}>📊 相关SQL</div>
              {logResult.sqls.map((s, i) => (
                <div key={i} style={{ fontSize: '12px', color: '#14532d', marginTop: '4px', whiteSpace: 'pre-wrap', fontFamily: 'JetBrains Mono, Consolas, monospace' }}>{s}</div>
              ))}
            </div>
          )}
          {logResult.tables && logResult.tables.length > 0 && (
            <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '12px' }}>
              涉及数据表：{logResult.tables.map((t, i) => (
                <span key={i} style={{ display: 'inline-block', background: '#eef2ff', borderRadius: '6px', padding: '3px 10px', margin: '3px 4px' }}>{t.name} ({t.desc})</span>
              ))}
            </div>
          )}
          {logResult.error && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', padding: '14px', color: '#991b1b' }}>❌ {logResult.error}</div>
          )}
        </div>
      )}

      {/* 搜索结果 */}
      {results && activeTab !== 'sql' && (
        <div>
          <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '12px' }}>
            共找到 <strong>{results.total}</strong> 条结果
            {results.total > (results.tables?.length || 0) && ` (显示前${results.tables.length}条)`}
          </div>
          {results.tables.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>
              未找到匹配结果
            </div>
          ) : (
            results.tables.map(table => (
              <TableCard
                key={table.name}
                table={table}
                expanded={expandedTable === table.name}
                onToggle={() => loadFullTable(table.name)}
                mode={activeTab}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}

function TableCard({ table, expanded, onToggle, mode }) {
  const matchedCount = table.matchedColumns?.length || 0;

  return (
    <div style={{
      border: '1px solid #e5e7eb', borderRadius: '12px',
      marginBottom: '12px', overflow: 'hidden',
      boxShadow: expanded ? '0 4px 12px rgba(0,0,0,0.08)' : 'none'
    }}>
      {/* 表头 */}
      <div
        onClick={onToggle}
        style={{
          padding: '14px 18px', cursor: 'pointer',
          background: expanded ? '#f9fafb' : '#fff',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          borderBottom: expanded ? '1px solid #e5e7eb' : 'none',
          transition: 'background 0.2s'
        }}
        onMouseEnter={e => e.currentTarget.style.background = '#f3f4f6'}
        onMouseLeave={e => e.currentTarget.style.background = expanded ? '#f9fafb' : '#fff'}
      >
        <div>
          <span style={{ fontFamily: 'JetBrains Mono, Consolas, monospace', fontSize: '15px', fontWeight: '600', color: '#1f2937' }}>
            {table.name}
          </span>
          {table.desc && (
            <span style={{ marginLeft: '10px', fontSize: '13px', color: '#6b7280' }}>{table.desc}</span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {mode === 'column' && matchedCount > 0 && (
            <span style={{ fontSize: '12px', color: '#4f46e5', background: '#eef2ff', padding: '2px 8px', borderRadius: '10px' }}>
              {matchedCount}个匹配字段
            </span>
          )}
          <span style={{ fontSize: '12px', color: '#9ca3af' }}>{table.colCount}列</span>
          <span style={{ fontSize: '12px', color: '#9ca3af' }}>{expanded ? '▲' : '▼'}</span>
        </div>
      </div>

      {/* 展开的表格内容 */}
      {expanded && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: '#f9fafb' }}>
                <th style={thStyle}>序号</th>
                <th style={thStyle}>列名</th>
                <th style={thStyle}>数据类型</th>
                <th style={thStyle}>长度</th>
                <th style={thStyle}>主键</th>
                <th style={thStyle}>可空</th>
                <th style={thStyle}>默认值</th>
                <th style={{ ...thStyle, textAlign: 'left', minWidth: '150px' }}>说明</th>
              </tr>
            </thead>
            <tbody>
              {(table.matchedColumns || table.columns || []).map((col, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={tdStyle}>{col.seq || i + 1}</td>
                  <td style={{ ...tdStyle, fontFamily: 'JetBrains Mono, Consolas, monospace', fontWeight: col.pk ? '600' : '400', color: col.pk ? '#4f46e5' : '#374151' }}>
                    {col.name}
                  </td>
                  <td style={{ ...tdStyle, color: '#6b7280' }}>{col.type}</td>
                  <td style={tdStyle}>{col.length}</td>
                  <td style={tdStyle}>{col.pk ? <span style={{ color: '#dc2626' }}>PK</span> : ''}</td>
                  <td style={tdStyle}>{col.nullable ? '' : <span style={{ color: '#2563eb' }}>NOT NULL</span>}</td>
                  <td style={{ ...tdStyle, color: '#9ca3af', maxWidth: '80px' }}>{col.default}</td>
                  <td style={{ ...tdStyle, textAlign: 'left' }}>{col.comment || col.label}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const thStyle = {
  padding: '8px 12px',
  textAlign: 'left',
  fontWeight: '600',
  color: '#374151',
  fontSize: '12px',
  whiteSpace: 'nowrap'
};

const tdStyle = {
  padding: '7px 12px',
  whiteSpace: 'nowrap',
  color: '#374151'
};
